import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Users, Calendar, GitBranch, ClipboardList, RefreshCw, Shuffle, Zap, AlertTriangle, UserCheck, CalendarDays, CheckCircle, Brain } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import "../../marketing.css";

const MODULES = [
  { icon: Users, name: "Crew Master", desc: "Central repository for all crew profiles, qualifications, type ratings, medical validity and DGCA licence expiry tracking.", badge: "FOUNDATION", metric: "100%", metricLabel: "Live sync" },
  { icon: Calendar, name: "Roster Planner", desc: "Interactive Gantt-chart roster view with fatigue score overlays, duty period validation and one-click publishing.", badge: "GANTT LIVE", metric: "<2s", metricLabel: "Render time" },
  { icon: GitBranch, name: "Pairing Builder", desc: "AI-assisted pairing construction that validates every FDP against DGCA CAR-OPS (Nov 2025) rules — day ≤13h, night ≤10h — before the pairing is saved.", badge: "AI ASSIST", metric: "FDTL", metricLabel: "Auto-validated" },
  { icon: CalendarDays, name: "Monthly Scheduler", desc: "Full-month crew schedule builder with clash detection, leave integration and automatic FDTL headroom calculation.", badge: "MONTHLY", metric: "30d", metricLabel: "Forward view" },
  { icon: ClipboardList, name: "Compliance Board", desc: "Real-time compliance audit board showing every FDP, rest period and cumulative FTL limit across the active roster.", badge: "REAL-TIME", metric: "DGCA", metricLabel: "Phase 1-3" },
  { icon: RefreshCw, name: "Recovery Console", desc: "Disruption recovery workspace — find available crew, check rest compliance and reassign duties with a single click.", badge: "RECOVERY", metric: "<5min", metricLabel: "Recovery time" },
  { icon: Zap, name: "Scenario Simulator", desc: "AI-powered Claude scenario engine. Simulate fleet changes, disruptions or new routes and see fatigue impact before committing.", badge: "AI CLAUDE", metric: "∞", metricLabel: "Scenarios" },
  { icon: AlertTriangle, name: "Disruption Console", desc: "Live disruption management with priority queuing, crew impact scoring and cascade-effect visualisation.", badge: "OCC", metric: "Live", metricLabel: "Queue" },
  { icon: UserCheck, name: "Crew Deployment", desc: "Station deployment matrix showing crew positioning across all bases with ATPL currency and recency tracking.", badge: "MULTI-BASE", metric: "All", metricLabel: "Stations" },
  { icon: Shuffle, name: "Swap Requests", desc: "Digital crew swap workflow — crew request, supervisor approve, FDTL auto-validated, roster updated instantly.", badge: "DIGITAL", metric: "0", metricLabel: "Paper forms" },
];

const FDTL_RULES = [
  "Maximum FDP limits enforced at pairing build time",
  "Minimum rest period validation before every assignment",
  "28-day, 3-month and 12-month cumulative FTL tracking",
  "Night FDP and augmented crew calculations built-in",
  "DGCA SDMS occurrence report integration",
  "FRMS Phase 1, 2 and 3 progression pathway",
];

export default function CrewRosteringPage() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-20 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 50% at 40% 30%, rgba(59,130,246,0.09) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 live-dot" />
              <span className="text-[10px] text-blue-400 marketing-font-mono uppercase font-bold tracking-widest">Crew Rostering & FDTL</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black marketing-font-heading leading-tight mb-5">
              Intelligent crew rostering<br />
              <span className="text-blue-400">with DGCA compliance built in.</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl">
              10 integrated modules covering everything from crew profile management to AI scenario simulation — with every pairing, roster and duty period validated against <strong className="text-white">DGCA CAR-OPS (Nov 2025)</strong> in real time.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/marketing/demo" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5" data-testid="crew-demo-btn">
                Request Demo <ArrowRight size={16} />
              </Link>
              <Link to="/login" className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 px-7 py-3.5 transition-all">
                See Live Platform <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 10 MODULES GRID ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-[10px] text-blue-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">10 integrated modules</div>
            <h2 className="text-3xl font-black marketing-font-heading">The complete crew operations suite.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="bg-[#0c1929]/50 border border-blue-500/10 hover:border-blue-500/25 p-5 card-glow transition-all group">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <Icon size={14} className="text-blue-400" />
                    </div>
                    <div className="text-right">
                      <div className="text-base font-black text-white marketing-font-mono">{m.metric}</div>
                      <div className="text-[8px] text-slate-600 marketing-font-mono uppercase">{m.metricLabel}</div>
                    </div>
                  </div>
                  <div className="text-[9px] text-blue-400 marketing-font-mono uppercase tracking-widest font-bold mb-1">{m.badge}</div>
                  <div className="text-sm font-bold text-white marketing-font-heading mb-2">{m.name}</div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FDTL COMPLIANCE ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f] grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[10px] text-blue-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">DGCA CAR-OPS Jan 2024 — Enforced Nov 2025</div>
              <h2 className="text-3xl font-black marketing-font-heading mb-5">
                Compliance enforced<br /><span className="text-blue-400">at the moment of scheduling.</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">Not a post-hoc check. Every pairing is validated against the latest DGCA rules before it's saved — violations are impossible to create.</p>
              <div className="space-y-3">
                {[
                  "Day FDP ≤13h / Night FDP (WOCL 00:00–05:59) ≤10h",
                  "Rest = max(12h, preceding duty); post-night: 24h + 1 local night + 2h buffer",
                  "48 consecutive hours rest within any 168-hour rolling window (new Nov 2025)",
                  "Maximum 2 night duties per 7-day period (reduced from 4, Nov 2025)",
                  "Weekly flight time ≤35h in any 7 consecutive days",
                  "28-day block hours ≤100h (short-haul) / ≤125h (augmented)",
                  "Annual flight ≤1,000h; Annual duty ≤1,800h",
                  "FDP extensions banned during split duty (new Jan 2024)",
                  "DGCA SDMS occurrence report submission built-in",
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-400">
                    <CheckCircle size={13} className="text-blue-400 shrink-0 mt-0.5" /> {r}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {[
                { phase: "Phase 1", title: "Prescriptive FDTL Compliance", desc: "Day/night FDP enforcement, 168h weekly rest cycle, 2-per-week night cap, 35h weekly flight limit, Do Not Assign alerts. DGCA CAR-OPS Nov 2025.", color: "border-blue-500/20 bg-blue-500/5" },
                { phase: "Phase 2", title: "Predictive Risk Management", desc: "Bio-mathematical fatigue modelling, circadian rhythm scoring, 7-day pattern detection and AI crew recommendations.", color: "border-violet-500/20 bg-violet-500/5" },
                { phase: "Phase 3", title: "Full FRMS (Voluntary)", desc: "Scientific evidence library, hazard register, FRMS policy docs and DGCA audit-ready package. Voluntary as of 2025.", color: "border-green-500/20 bg-green-500/5" },
                { phase: "Phase 2", title: "Predictive Risk Engine", desc: "Bio-mathematical fatigue modelling, 7-day pattern detection and AI-assisted crew recommendations.", color: "border-violet-500/20 bg-violet-500/5" },
              ].map((p, i) => (
                <div key={i} className={`border ${p.color} p-5`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[9px] marketing-font-mono font-black uppercase tracking-widest text-slate-400 border border-white/10 px-2 py-1">{p.phase}</span>
                    <span className="font-bold text-white text-sm">{p.title}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── AI SECTION ── */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="bg-[#0c1929]/40 border border-violet-500/15 p-8 flex flex-col lg:flex-row items-start gap-8">
            <div className="w-12 h-12 bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0">
              <Brain size={22} className="text-violet-400" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] text-violet-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">AI Scenario Simulator — Claude Sonnet</div>
              <h3 className="text-xl font-black marketing-font-heading mb-3">Ask the AI: "What happens to crew fatigue if I add a Bangalore base?"</h3>
              <p className="text-slate-400 leading-relaxed mb-5">The Scenario Simulator uses Claude AI to model proposed changes — new routes, fleet additions, disruptions — and shows you the fatigue impact, FDTL exposure and staffing gap before you commit.</p>
              <Link to="/marketing/demo" className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 text-sm font-bold transition-colors">
                See it in action <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl font-black marketing-font-heading mb-4">Ready to fix your rostering?</h2>
          <p className="text-slate-400 max-w-md mx-auto mb-8">30-minute live walkthrough with your fleet data. FDTL calculations running live.</p>
          <Link to="/marketing/demo" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
            Request Demo <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
